class EnergyLinkLP {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('[data-energylink-form]');
    this.variantInput = root.querySelector('[data-variant-id]');
    this.selects = Array.from(root.querySelectorAll('[data-option-select]'));
    this.quantityInput = root.querySelector('[data-quantity]');
    this.addButton = root.querySelector('[data-add-to-cart]');
    this.addButtonText = root.querySelector('[data-add-to-cart-text]');
    this.availability = root.querySelector('[data-availability]');
    this.announcement = root.querySelector('[data-announcement]');
    this.heroImage = root.querySelector('[data-hero-image]');
    this.stickyButton = root.querySelector('[data-sticky-button]');
    this.stickyPrice = root.querySelector('[data-sticky-price]');
    this.buyTarget = root.querySelector('#energylink-buy');
    this.variantDataNode = root.querySelector('[data-energylink-variants]');
    this.variants = this.parseVariants();
    this.launchAt = this.parseLaunchDate(root.dataset.launchAt);
    this.blackValue = root.dataset.blackValue || 'ブラック';
    this.whiteValue = root.dataset.whiteValue || 'ホワイト';
    this.blackImage = root.dataset.blackImage || '';
    this.whiteImage = root.dataset.whiteImage || '';
    this.prelaunchText = root.dataset.prelaunchText || '8/3 12:00 販売開始';
    this.availableText = root.dataset.availableText || 'カートに入れる';
    this.soldoutText = root.dataset.soldoutText || '完売';
    this.isPrelaunch = false;
    this.currentVariant = null;

    if (!this.form || !this.variantDataNode || !this.variants.length) return;

    this.bindEvents();
    this.syncSelectors();
    this.updateState();
    this.updateLaunchState();
    this.launchTimer = window.setInterval(() => this.updateLaunchState(), 30000);
  }

  parseVariants() {
    if (!this.variantDataNode) return [];

    try {
      return JSON.parse(this.variantDataNode.textContent);
    } catch (error) {
      console.warn('EnergyLink variant data could not be parsed.', error);
      return [];
    }
  }

  parseLaunchDate(value) {
    if (!value) return null;
    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? null : timestamp;
  }

  bindEvents() {
    this.selects.forEach((select) => {
      select.addEventListener('change', () => {
        this.syncSelectors();
        this.updateState();
      });
    });

    if (this.stickyButton) {
      this.stickyButton.addEventListener('click', () => {
        if (!this.buyTarget) return;
        this.buyTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => {
          const firstSelect = this.selects.find((select) => !select.disabled);
          if (firstSelect) firstSelect.focus({ preventScroll: true });
        }, 550);
      });
    }

    this.root.querySelectorAll('[data-scroll-to-buy]').forEach((button) => {
      button.addEventListener('click', () => {
        if (this.buyTarget) {
          this.buyTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    });
  }

  getSelectedOptions() {
    return this.selects.map((select) => select.value);
  }

  syncSelectors() {
    const selected = this.getSelectedOptions();

    this.selects.forEach((select, index) => {
      Array.from(select.options).forEach((option) => {
        const canExist = this.variants.some((variant) => {
          if (!variant.available) return false;
          if (variant.options[index] !== option.value) return false;

          for (let previousIndex = 0; previousIndex < index; previousIndex += 1) {
            if (variant.options[previousIndex] !== selected[previousIndex]) return false;
          }

          return true;
        });

        option.disabled = !canExist;
      });

      if (select.selectedOptions[0] && select.selectedOptions[0].disabled) {
        const firstEnabled = Array.from(select.options).find((option) => !option.disabled);
        if (firstEnabled) {
          select.value = firstEnabled.value;
          selected[index] = firstEnabled.value;
        }
      }
    });
  }

  findVariant() {
    const selected = this.getSelectedOptions();
    return this.variants.find((variant) => (
      variant.options.length === selected.length &&
      variant.options.every((value, index) => value === selected[index])
    )) || null;
  }

  updateState() {
    this.currentVariant = this.findVariant();

    if (this.currentVariant && this.variantInput) {
      this.variantInput.value = this.currentVariant.id;
    }

    this.updateAvailability();
    this.updateHeroImage();
    this.updateButton();
  }

  updateAvailability() {
    if (!this.availability) return;

    if (!this.currentVariant) {
      this.availability.textContent = 'この組み合わせは販売していません。';
      this.availability.dataset.state = 'soldout';
      return;
    }

    if (!this.currentVariant.available) {
      this.availability.textContent = '選択中のバリエーションは完売しました。';
      this.availability.dataset.state = 'soldout';
      return;
    }

    const tracksInventory = this.currentVariant.inventory_management === 'shopify';
    const inventory = Number(this.currentVariant.inventory_quantity);

    if (tracksInventory && Number.isFinite(inventory) && inventory >= 0) {
      this.availability.textContent = `選択中のバリエーション：残り${inventory}点`;
      this.availability.dataset.state = inventory <= 5 ? 'low' : 'available';
      return;
    }

    this.availability.textContent = '選択中のバリエーション：在庫あり';
    this.availability.dataset.state = 'available';
  }

  updateHeroImage() {
    if (!this.heroImage || !this.selects.length) return;

    const colorSelect = this.selects.find((select) => (
      /カラー|color/i.test(select.dataset.optionName || '')
    )) || this.selects[0];

    const color = colorSelect.value;
    let nextSource = '';

    if (color === this.whiteValue && this.whiteImage) {
      nextSource = this.whiteImage;
    } else if (color === this.blackValue && this.blackImage) {
      nextSource = this.blackImage;
    }

    if (!nextSource || this.heroImage.getAttribute('src') === nextSource) return;

    this.heroImage.classList.add('is-changing');
    const preload = new Image();
    preload.onload = () => {
      this.heroImage.setAttribute('src', nextSource);
      this.heroImage.removeAttribute('srcset');
      this.heroImage.classList.remove('is-changing');
    };
    preload.onerror = () => {
      this.heroImage.classList.remove('is-changing');
    };
    preload.src = nextSource;
  }

  updateLaunchState() {
    this.isPrelaunch = Boolean(this.launchAt && Date.now() < this.launchAt);

    if (this.announcement) {
      this.announcement.textContent = this.isPrelaunch
        ? this.announcement.dataset.beforeLaunch
        : this.announcement.dataset.afterLaunch;
    }

    this.updateButton();
  }

  updateButton() {
    const unavailable = !this.currentVariant || !this.currentVariant.available;

    if (this.addButton) {
      this.addButton.disabled = this.isPrelaunch || unavailable;
    }

    if (this.addButtonText) {
      if (this.isPrelaunch) {
        this.addButtonText.textContent = this.prelaunchText;
      } else if (unavailable) {
        this.addButtonText.textContent = this.soldoutText;
      } else {
        this.addButtonText.textContent = this.availableText;
      }
    }

    if (this.stickyButton) {
      this.stickyButton.disabled = false;
      this.stickyButton.textContent = this.isPrelaunch
        ? this.prelaunchText
        : (unavailable ? this.soldoutText : 'カラー・サイズを選ぶ');
    }

    if (this.stickyPrice) {
      this.stickyPrice.hidden = this.isPrelaunch;
    }
  }
}

const initEnergyLinkSections = (scope = document) => {
  scope.querySelectorAll('[data-energylink-root]').forEach((root) => {
    if (root.dataset.energylinkInitialized === 'true') return;
    root.dataset.energylinkInitialized = 'true';
    new EnergyLinkLP(root);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initEnergyLinkSections());
} else {
  initEnergyLinkSections();
}

document.addEventListener('shopify:section:load', (event) => {
  initEnergyLinkSections(event.target);
});
/*
 * EnergyLink 追加購入フォーム
 * TOPの購入フォームを中段・最下部へ複製し、
 * 選択中のカラー・サイズ・在庫・バリエーションを同期します。
 */

(() => {
  const rewriteCloneIds = (clone, suffix) => {
    const idMap = new Map();

    clone.querySelectorAll('[id]').forEach((element) => {
      const oldId = element.id;
      const newId = `${oldId}-${suffix}`;

      idMap.set(oldId, newId);
      element.id = newId;
    });

    clone.querySelectorAll('label[for]').forEach((label) => {
      const newFor = idMap.get(label.htmlFor);

      if (newFor) {
        label.htmlFor = newFor;
      }
    });
  };

  const initEnergyLinkPurchaseClones = (scope = document) => {
    const roots = [];

    if (scope.matches && scope.matches('[data-energylink-root]')) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(...scope.querySelectorAll('[data-energylink-root]'));
    }

    roots.forEach((root) => {
      if (root.dataset.purchaseClonesInitialized === 'true') {
        return;
      }

      const sourceBox = root.querySelector('#energylink-buy');

      const placeholders = Array.from(
        root.querySelectorAll('[data-energylink-purchase-clone]')
      );

      if (!sourceBox || !placeholders.length) {
        return;
      }

      const cloneBoxes = placeholders.map((placeholder, index) => {
        const position =
          placeholder.dataset.energylinkPurchaseClone ||
          `position-${index + 1}`;

        const suffix = position.replace(/[^a-zA-Z0-9_-]/g, '-');
        const clone = sourceBox.cloneNode(true);

        clone.id = `energylink-buy-${suffix}`;
        clone.classList.add('energylink-lp__form--embedded');
        clone.dataset.energylinkClonedBuybox = suffix;

        if (position === 'final') {
          clone.classList.add('energylink-lp__form--final');

          clone
            .querySelector('.energylink-lp__price-row')
            ?.remove();

          clone
            .querySelector('.energylink-lp__form-disclaimer')
            ?.remove();
        }

        rewriteCloneIds(clone, suffix);
        placeholder.replaceChildren(clone);

        return clone;
      });

      const sourceSelects = Array.from(
        sourceBox.querySelectorAll('[data-option-select]')
      );

      const sourceVariantInput =
        sourceBox.querySelector('[data-variant-id]');

      const sourceAvailability =
        sourceBox.querySelector('[data-availability]');

      const sourceButton =
        sourceBox.querySelector('[data-add-to-cart]');

      const sourceButtonText =
        sourceBox.querySelector('[data-add-to-cart-text]');

      let syncing = false;

      const copySourceStateToClone = (cloneBox) => {
        const cloneSelects = Array.from(
          cloneBox.querySelectorAll('[data-option-select]')
        );

        cloneSelects.forEach((cloneSelect, selectIndex) => {
          const sourceSelect = sourceSelects[selectIndex];

          if (!sourceSelect) {
            return;
          }

          Array.from(cloneSelect.options).forEach(
            (cloneOption, optionIndex) => {
              const sourceOption =
                sourceSelect.options[optionIndex];

              cloneOption.disabled = sourceOption
                ? sourceOption.disabled
                : false;
            }
          );

          cloneSelect.value = sourceSelect.value;
        });

        const cloneVariantInput =
          cloneBox.querySelector('[data-variant-id]');

        if (cloneVariantInput && sourceVariantInput) {
          cloneVariantInput.value = sourceVariantInput.value;
        }

        const cloneAvailability =
          cloneBox.querySelector('[data-availability]');

        if (cloneAvailability && sourceAvailability) {
          cloneAvailability.textContent =
            sourceAvailability.textContent;

          if (sourceAvailability.dataset.state) {
            cloneAvailability.dataset.state =
              sourceAvailability.dataset.state;
          } else {
            delete cloneAvailability.dataset.state;
          }
        }

        const cloneButton =
          cloneBox.querySelector('[data-add-to-cart]');

        if (cloneButton && sourceButton) {
          cloneButton.disabled = sourceButton.disabled;
        }

        const cloneButtonText =
          cloneBox.querySelector('[data-add-to-cart-text]');

        if (cloneButtonText && sourceButtonText) {
          cloneButtonText.textContent =
            sourceButtonText.textContent;
        }
      };

      const syncClonesFromSource = () => {
        cloneBoxes.forEach(copySourceStateToClone);
      };

      sourceSelects.forEach((sourceSelect) => {
        sourceSelect.addEventListener('change', () => {
          if (syncing) {
            return;
          }

          window.queueMicrotask(syncClonesFromSource);
        });
      });

      cloneBoxes.forEach((cloneBox) => {
        const cloneSelects = Array.from(
          cloneBox.querySelectorAll('[data-option-select]')
        );

        cloneSelects.forEach((cloneSelect, changedIndex) => {
          cloneSelect.addEventListener('change', () => {
            if (syncing) {
              return;
            }

            syncing = true;

            sourceSelects.forEach((sourceSelect, index) => {
              if (cloneSelects[index]) {
                sourceSelect.value =
                  cloneSelects[index].value;
              }
            });

            if (sourceSelects[changedIndex]) {
              sourceSelects[changedIndex].dispatchEvent(
                new Event('change', {
                  bubbles: true
                })
              );
            }

            syncing = false;

            window.requestAnimationFrame(
              syncClonesFromSource
            );
          });
        });
      });

      const observer = new MutationObserver(() => {
        if (!syncing) {
          syncClonesFromSource();
        }
      });

      observer.observe(sourceBox, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: [
          'disabled',
          'data-state'
        ]
      });

      /*
       * スマホ下部の固定購入ボタンを押した際、
       * TOPではなく現在位置より下にある購入フォームへ移動
       */
      const stickyButton =
        root.querySelector('[data-sticky-button]');

      if (stickyButton) {
        stickyButton.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();

            const buyBoxes = [
              sourceBox,
              ...cloneBoxes
            ];

            const nextBox =
              buyBoxes.find((box) => {
                return (
                  box.getBoundingClientRect().top > 120
                );
              }) ||
              buyBoxes[buyBoxes.length - 1];

            nextBox.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          },
          true
        );
      }

      root.dataset.purchaseClonesInitialized = 'true';

      syncClonesFromSource();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initEnergyLinkPurchaseClones();
      }
    );
  } else {
    initEnergyLinkPurchaseClones();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initEnergyLinkPurchaseClones(event.target);
    }
  );
})();

(() => {
  const initEnergyLinkPurchaseImages = (scope = document) => {
    const roots = [];

    if (scope.matches && scope.matches('[data-energylink-root]')) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(
        ...scope.querySelectorAll('[data-energylink-root]')
      );
    }

    roots.forEach((root) => {
      if (
        root.dataset.purchaseImageSyncInitialized ===
        'true'
      ) {
        return;
      }

      const sourceImage =
        root.querySelector('[data-hero-image]');

      const placeholders = Array.from(
        root.querySelectorAll(
          '[data-energylink-purchase-clone]'
        )
      );

      if (!sourceImage || !placeholders.length) {
        return;
      }

      placeholders.forEach((placeholder) => {
        if (
          placeholder.querySelector(
            '[data-energylink-clone-image]'
          )
        ) {
          return;
        }

        const media = document.createElement('div');
        const image = sourceImage.cloneNode(true);

        media.className =
          'energylink-lp__purchase-image';

        image.removeAttribute('data-hero-image');
        image.removeAttribute('fetchpriority');
        image.setAttribute('loading', 'lazy');
        image.dataset.energylinkCloneImage = '';

        media.appendChild(image);
        placeholder.prepend(media);
      });

      const cloneImages = Array.from(
        root.querySelectorAll(
          '[data-energylink-clone-image]'
        )
      );

      const syncImages = () => {
        cloneImages.forEach((image) => {
          ['src', 'srcset', 'sizes', 'alt'].forEach(
            (attribute) => {
              if (sourceImage.hasAttribute(attribute)) {
                image.setAttribute(
                  attribute,
                  sourceImage.getAttribute(attribute)
                );
              } else {
                image.removeAttribute(attribute);
              }
            }
          );
        });
      };

      const observer = new MutationObserver(syncImages);

      observer.observe(sourceImage, {
        attributes: true,
        attributeFilter: [
          'src',
          'srcset',
          'sizes',
          'alt'
        ]
      });

      root.dataset.purchaseImageSyncInitialized =
        'true';

      syncImages();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initEnergyLinkPurchaseImages();
      }
    );
  } else {
    initEnergyLinkPurchaseImages();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initEnergyLinkPurchaseImages(event.target);
    }
  );
})();

(() => {
  const initVariantImageSync = (scope = document) => {
    const roots = [];

    if (
      scope.matches &&
      scope.matches('[data-energylink-root]')
    ) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(
        ...scope.querySelectorAll('[data-energylink-root]')
      );
    }

    roots.forEach((root) => {
      if (
        root.dataset.variantImageSyncInitialized ===
        'true'
      ) {
        return;
      }

      const sourceImage = root.querySelector(
        '#energylink-buy [data-hero-image]'
      );

      const cloneImages = Array.from(
        root.querySelectorAll(
          '[data-energylink-purchase-clone] [data-hero-image]'
        )
      );

      if (!sourceImage || !cloneImages.length) {
        return;
      }

      cloneImages.forEach((image) => {
        image.removeAttribute('data-hero-image');
        image.removeAttribute('fetchpriority');
        image.setAttribute('loading', 'lazy');
        image.dataset.energylinkCloneImage = '';
      });

      const syncImages = () => {
        cloneImages.forEach((image) => {
          ['src', 'srcset', 'sizes', 'alt'].forEach(
            (attribute) => {
              if (sourceImage.hasAttribute(attribute)) {
                image.setAttribute(
                  attribute,
                  sourceImage.getAttribute(attribute)
                );
              } else {
                image.removeAttribute(attribute);
              }
            }
          );
        });
      };

      const observer = new MutationObserver(syncImages);

      observer.observe(sourceImage, {
        attributes: true,
        attributeFilter: [
          'src',
          'srcset',
          'sizes',
          'alt'
        ]
      });

      root.dataset.variantImageSyncInitialized =
        'true';

      syncImages();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initVariantImageSync();
      }
    );
  } else {
    initVariantImageSync();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initVariantImageSync(event.target);
    }
  );
})();


(() => {
  const initPurchaseVariantImages = (
    scope = document
  ) => {
    const roots = [];

    if (
      scope.matches &&
      scope.matches('[data-energylink-root]')
    ) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(
        ...scope.querySelectorAll(
          '[data-energylink-root]'
        )
      );
    }

    roots.forEach((root) => {
      if (
        root.dataset.purchaseVariantImagesInitialized ===
        'true'
      ) {
        return;
      }

      const buyBoxes = Array.from(
        root.querySelectorAll(
          '[data-energylink-buybox]'
        )
      );

      const blackImage = root.dataset.blackImage || '';
      const whiteImage = root.dataset.whiteImage || '';

      if (
        !buyBoxes.length ||
        (!blackImage && !whiteImage)
      ) {
        return;
      }

      const images = buyBoxes.map((buyBox) => {
        let wrapper = buyBox.querySelector(
          ':scope > .energylink-lp__purchase-image'
        );

        if (!wrapper) {
          wrapper = document.createElement('div');
          wrapper.className =
            'energylink-lp__purchase-image';

          const image = document.createElement('img');

          image.loading = 'lazy';
          image.decoding = 'async';
          image.alt = 'EnergyLink 選択中の商品';

          wrapper.appendChild(image);
          buyBox.prepend(wrapper);
        }

        return wrapper.querySelector('img');
      });

      const getSelectedColor = () => {
        const referenceBox =
          root.querySelector('#energylink-buy');

        if (!referenceBox) {
          return '';
        }

        const selects = Array.from(
          referenceBox.querySelectorAll(
            '[data-option-select]'
          )
        );

        const colorSelect =
          selects.find((select) => {
            return /カラー|color/i.test(
              select.dataset.optionName || ''
            );
          }) || selects[0];

        return colorSelect ? colorSelect.value : '';
      };

      const updateImages = () => {
        const selectedColor = getSelectedColor();

        const isWhite =
          selectedColor === root.dataset.whiteValue;

        const imageUrl = isWhite
          ? whiteImage || blackImage
          : blackImage || whiteImage;

        if (!imageUrl) {
          return;
        }

        images.forEach((image) => {
          image.src = imageUrl;
          image.alt = isWhite
            ? 'EnergyLink ホワイト'
            : 'EnergyLink ブラック';
        });
      };

      root.addEventListener('change', (event) => {
        if (
          event.target.matches('[data-option-select]')
        ) {
          window.requestAnimationFrame(updateImages);
        }
      });

      root.dataset.purchaseVariantImagesInitialized =
        'true';

      updateImages();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initPurchaseVariantImages();
      }
    );
  } else {
    initPurchaseVariantImages();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initPurchaseVariantImages(event.target);
    }
  );
})();
